/**
 * Image Compression Utility
 * Compresses images before upload to save storage
 * Converts MB-sized images to KB-sized compressed versions
 */

/**
 * Compress an image file
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File>} - Compressed file
 */
export async function compressImage(file, options = {}) {
  // Default compression options
  const defaultOptions = {
    maxSizeMB: 0.3,           // Max size 300KB (WhatsApp-like compression)
    maxWidthOrHeight: 1920,    // Max dimension
    useWebWorker: true,
    fileType: undefined,       // Auto-detect (converts to JPEG for better compression)
    initialQuality: 0.8        // Quality (0.1 to 1.0)
  };

  const compressionOptions = { ...defaultOptions, ...options };

  try {
    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      console.log('📄 Not an image file, skipping compression:', file.name);
      return file;
    }

    // Dynamic import of browser-image-compression
    const imageCompression = (await import('browser-image-compression')).default;

    console.log('🖼️ Original image:', {
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      type: file.type
    });

    // Compress the image
    const compressedFile = await imageCompression(file, compressionOptions);

    // Calculate compression ratio
    const compressionRatio = ((1 - compressedFile.size / file.size) * 100).toFixed(1);

    console.log('✅ Compressed image:', {
      name: compressedFile.name,
      size: `${(compressedFile.size / 1024).toFixed(2)} KB`,
      compressionRatio: `${compressionRatio}% smaller`,
      type: compressedFile.type
    });

    // Return compressed file with original name
    return new File([compressedFile], file.name, {
      type: compressedFile.type,
      lastModified: Date.now()
    });

  } catch (error) {
    console.error('❌ Image compression failed:', error);
    console.log('⚠️ Using original file');
    return file; // Return original file if compression fails
  }
}

/**
 * Compress multiple images
 * @param {FileList|File[]} files - Array of files to compress
 * @param {Object} options - Compression options
 * @returns {Promise<File[]>} - Array of compressed files
 */
export async function compressImages(files, options = {}) {
  const fileArray = Array.from(files);
  const compressionPromises = fileArray.map(file => compressImage(file, options));
  return Promise.all(compressionPromises);
}

/**
 * Compress image from camera capture
 * For higher compression on mobile photos
 * @param {File} file - Camera captured image file
 * @returns {Promise<File>} - Compressed file
 */
export async function compressCameraImage(file) {
  return compressImage(file, {
    maxSizeMB: 0.2,           // More aggressive for camera (200KB)
    maxWidthOrHeight: 1280,    // Lower resolution for camera
    initialQuality: 0.75       // Slightly lower quality
  });
}

/**
 * Compress signature image
 * For very small file size
 * @param {File} file - Signature image file
 * @returns {Promise<File>} - Compressed file
 */
export async function compressSignature(file) {
  return compressImage(file, {
    maxSizeMB: 0.1,           // Very small (100KB)
    maxWidthOrHeight: 800,     // Signature doesn't need high resolution
    initialQuality: 0.7        // Lower quality acceptable for signatures
  });
}

/**
 * Compress document/KYC image
 * Balance between quality and size
 * @param {File} file - Document image file
 * @returns {Promise<File>} - Compressed file
 */
export async function compressDocument(file) {
  return compressImage(file, {
    maxSizeMB: 0.4,           // Medium compression (400KB)
    maxWidthOrHeight: 2048,    // Keep good quality for documents
    initialQuality: 0.85       // Higher quality for readability
  });
}

/**
 * Check if file needs compression
 * @param {File} file - File to check
 * @param {number} thresholdMB - Threshold in MB (default 0.5MB)
 * @returns {boolean} - True if file needs compression
 */
export function needsCompression(file, thresholdMB = 0.5) {
  return file.size > thresholdMB * 1024 * 1024;
}

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted size string
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

export default {
  compressImage,
  compressImages,
  compressCameraImage,
  compressSignature,
  compressDocument,
  needsCompression,
  formatFileSize
};


